import { Correctness } from '@showbie-socrative/socrative-utils';
import {
  QueryClient,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import produce from 'immer';

import { StudentScore } from '~teacher/models/activities';

import { upsertStudentScore } from '../../api/studentScore';
import { ActivityResponse } from './useActivity';

export const updateStudentScore =
  ({
    studentScore,
    questionId,
  }: {
    studentScore: StudentScore;
    questionId: number;
  }) =>
  (activity: ActivityResponse) => {
    const nextActivity = produce(activity, (nextActivity) => {
      if (!nextActivity.questionScores[questionId]) {
        throw Error('Question score does not exist.');
      }
      nextActivity.questionScores[questionId].studentScores[
        studentScore.studentId
      ] = studentScore;
    });
    return nextActivity;
  };

export function updateStudentScoreCache({
  queryClient,
  activityInstanceId,
  studentScore,
  questionId: questionId,
}: {
  queryClient: QueryClient;
  activityInstanceId: number;
  studentScore: StudentScore;
  questionId: number;
}) {
  queryClient.setQueryData(
    ['activity', activityInstanceId],
    updateStudentScore({
      studentScore: studentScore,
      questionId: questionId,
    })
  );
}

export type UpsertStudentScoreMutationParameters = {
  questionScoreId: number;
  isRostered: boolean;
  studentId: number;
  score: number;
  correctness: Correctness;
};

export function useUpsertStudentScore({
  activityInstanceId,
  questionId,
}: {
  activityInstanceId: number;
  questionId: number;
}) {
  const queryClient = useQueryClient();

  return useMutation(
    ({
      questionScoreId,
      isRostered,
      studentId,
      score,
      correctness,
    }: UpsertStudentScoreMutationParameters) => {
      return upsertStudentScore({
        questionScoreId,
        isRostered,
        studentId,
        score,
        correctness,
      });
    },
    {
      useErrorBoundary: true,
      onSuccess: (data) => {
        updateStudentScoreCache({
          queryClient: queryClient,
          activityInstanceId: activityInstanceId,
          studentScore: { ...data, updatedDate: new Date(data.updatedDate) },
          questionId: questionId,
        });
      },
    }
  );
}
